import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabaseClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Calendar, ClipboardList, Warehouse } from "lucide-react";
import UpcomingDraftsWidget from "@/components/UpcomingDraftsWidget";

/**
 * Stub dashboard — counts visible bookings / sites / bays per RLS.
 * Real widgets land in a later phase.
 */
export default function Dashboard() {
  const { profile, user, roles, hasRole } = useAuth();
  const [counts, setCounts] = useState({ bookings: null, sites: null, bays: null });

  useEffect(() => {
    let mounted = true;
    (async () => {
      const safe = (p) => p.then((r) => r.count ?? 0).catch(() => 0);
      const [bookings, sites, bays] = await Promise.all([
        safe(supabase.from("lbs_bookings").select("id", { count: "exact", head: true })),
        safe(supabase.from("lbs_sites").select("id", { count: "exact", head: true })),
        safe(supabase.from("lbs_bays").select("id", { count: "exact", head: true })),
      ]);
      if (!mounted) return;
      setCounts({ bookings, sites, bays });
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Drivers only have jobs to look at — send them straight to My jobs.
  if (roles.length && roles.every((r) => r === "driver")) return <Navigate to="/driver" replace />;
  const canSites = hasRole(["super_admin", "client_admin", "site_admin"]);
  const canBays = hasRole(["super_admin", "client_admin", "site_admin", "dock_manager", "receiving", "dispatch", "gate"]);

  const greeting =
    profile?.first_name ? `Welcome back, ${profile.first_name}` : `Welcome, ${user?.email}`;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{greeting}</h1>
          <p className="text-sm text-slate-500">
            Your role-scoped overview of bookings, sites and bays.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {roles.map((r) => (
            <Badge key={r} variant="outline" data-testid={`role-${r}`}>
              {r.replace("_", " ")}
            </Badge>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Bookings (visible)" value={counts.bookings} icon={Calendar} testid="stat-bookings" to="/bookings" />
        <StatCard label="Sites (visible)" value={counts.sites} icon={Building2} testid="stat-sites" to={canSites ? "/sites" : undefined} />
        <StatCard label="Bays (visible)" value={counts.bays} icon={Warehouse} testid="stat-bays" to={canBays ? "/bay-view" : undefined} />
        <StatCard label="Open tasks" value={"—"} icon={ClipboardList} testid="stat-tasks" />
      </div>

      <UpcomingDraftsWidget />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid, to }) {
  const body = (
    <Card data-testid={testid} className={to ? "transition-colors hover:border-slate-400 hover:bg-slate-50 cursor-pointer" : ""}>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-slate-100 p-2">
            <Icon className="h-4 w-4 text-slate-700" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
            <div className="text-2xl font-semibold tabular-nums">
              {value === null ? "…" : value}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} data-testid={`${testid}-link`}>{body}</Link> : body;
}
